import { EmeraldIcon } from "@emerald-react/icon";
import { useEffect, useRef, useState } from "react";

export interface MediaDropdownFile {
  mediaId: string;
  displayName?: string;
  keyName?: string;
  cloudfrontUrl?: string;
  s3Url?: string;
}

export interface MediaDropdownSection {
  label: string;
  files: MediaDropdownFile[];
}

interface MediaDropdownPillProps {
  icon: string;
  label: string;
  files?: MediaDropdownFile[];
  sections?: MediaDropdownSection[];
}

function splitExt(name: string): [string, string] {
  const dot = name.lastIndexOf(".");
  if (dot > 0) return [name.slice(0, dot), name.slice(dot)];
  return [name, ""];
}

function FileLink({
  f,
  onClose,
}: {
  f: MediaDropdownFile;
  onClose: () => void;
}) {
  const fullName = f.displayName || f.keyName || f.mediaId;
  const [stem, ext] = splitExt(fullName);
  return (
    <a
      key={f.mediaId}
      href={f.cloudfrontUrl || f.s3Url}
      title={fullName}
      target="_blank"
      rel="noopener noreferrer"
      className="media-dropdown-item"
      onClick={onClose}
    >
      <span className="truncate min-w-0">{stem}</span>
      {ext && <span className="shrink-0">{ext}</span>}
    </a>
  );
}

const MediaDropdownPill = ({
  icon,
  label,
  files,
  sections,
}: MediaDropdownPillProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="media-dropdown-trigger"
      >
        <EmeraldIcon icon={icon} type="filled" variant="icon" />
        {label}
      </button>

      {open && (
        <div className="media-dropdown-menu">
          {sections
            ? sections.map((section, sIdx) => (
                <div key={section.label}>
                  {sIdx > 0 && (
                    <div className="my-1 border-t border-gray-300" />
                  )}
                  <p
                    className="px-3 pt-2 pb-1 text-xs font-medium"
                    style={{ color: "var(--color-data-muted)" }}
                  >
                    {section.label}
                  </p>
                  {section.files.map((f) => (
                    <FileLink
                      key={f.mediaId}
                      f={f}
                      onClose={() => setOpen(false)}
                    />
                  ))}
                </div>
              ))
            : files?.map((f) => (
                <FileLink
                  key={f.mediaId}
                  f={f}
                  onClose={() => setOpen(false)}
                />
              ))}
        </div>
      )}
    </div>
  );
};

export default MediaDropdownPill;
